import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import EditableCell from '../common/EditableCell';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import DevelopmentAnalyticsDashboard from './DevelopmentAnalyticsDashboard';

// Shared minimum width for the log's header and body tables. They are separate
// <table> elements, so they must agree on a width or the fixed header would drift
// out of alignment with the rows once the card scrolls sideways on a phone.
const LOG_TABLE_MIN_WIDTH = 620;

const COLUMNS = [
  { key: 'timestamp', label: 'Timestamp', type: 'datetime', width: 155, fixed: true },
  { key: 'user_name', label: 'User', type: 'text', width: 100, fixed: true },
  { key: 'modifications', label: 'Modifications', type: 'textarea', width: 'auto' },
  { key: 'team_comments', label: 'Team Comments', type: 'textarea', width: 'auto' },
];

// Format date to local time
const formatTimestamp = (dateVal) => {
  if (!dateVal) return '-';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  });
};

// Get current time for display
const getCurrentTime = () => {
  return new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const DevelopmentLog = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const fetchItemsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchItemsRef.current) fetchItemsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('developmentLog', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/development-log');
      if (response.data.success) setItems(response.data.data || []);
      else setError('Failed to load development log');
    } catch (err) { console.error('Error:', err); setError('Failed to load data. Please try again.'); }
    finally { setLoading(false); }
  }, [instance, accounts]);

  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSaveCell = async (rowId, columnKey, newValue) => {
    setSavingRows(prev => new Set(prev).add(rowId));
    try {
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/development-log/${rowId}`, { [columnKey]: newValue }, syncHeaders());
      if (response.data.success) { setItems(prev => prev.map(item => item.id === rowId ? { ...item, [columnKey]: newValue } : item)); showToast('Saved successfully'); }
      else throw new Error(response.data.message || 'Save failed');
    } catch (err) { showToast(`Save failed: ${err.message}`, 'error'); throw err; }
    finally { setSavingRows(prev => { const s = new Set(prev); s.delete(rowId); return s; }); }
  };

  // Get current user's display name from MSAL
  const currentUserName = accounts[0]?.name || accounts[0]?.username || 'Unknown User';

  const handleAddNewRow = async () => {
    try {
      const rowData = { 
        ...newRow,
        timestamp: new Date().toISOString(),
        user_name: currentUserName
      };
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/development-log', rowData, syncHeaders());
      if (response.data.success) { setItems(prev => [response.data.data, ...prev]); setNewRow({}); setIsAddingNew(false); showToast('New log entry added'); }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Failed: ${err.message}`, 'error'); }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Delete this log entry?')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/development-log/${rowId}`, null, syncHeaders());
      if (response.data.success) { setItems(prev => prev.filter(item => item.id !== rowId)); showToast('Deleted'); }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Delete failed: ${err.message}`, 'error'); }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/development-log/send-report');
      if (response.data.success) showToast('Report email triggered — check your inbox shortly');
      else throw new Error(response.data.message || 'Failed to send report');
    } catch (err) { showToast(`Report failed: ${err.message}`, 'error'); }
    finally { setSendingReport(false); }
  };

  const filteredItems = items.filter(item => !searchTerm || [item.user_name, item.modifications, item.team_comments].some(v => (v || '').toLowerCase().includes(searchTerm.toLowerCase())));

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div style={{ display: 'inline-block', width: '48px', height: '48px', border: '4px solid #f3f3f3', borderTop: '4px solid #17a2b8', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div><p style={{ marginTop: '1rem', color: '#666' }}>Loading development log...</p><style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style></div>;
  if (error) return <div style={{ padding: '2rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', color: '#856404' }}><strong>⚠️ Error:</strong> {error}<button onClick={fetchItems} style={{ marginLeft: '1rem', padding: '0.5rem 1rem', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retry</button></div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {toast.show && <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', color: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>{toast.message}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h3 style={{ margin: 0, color: '#333' }}>🛠️ Development Log</h3>
          <span style={{ backgroundColor: '#6c757d', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem' }}>{filteredItems.length} entries</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <div style={{ position: 'relative' }}><input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '8px 12px 8px 36px', border: '1px solid #dee2e6', borderRadius: '4px', width: '200px', fontSize: '0.9rem' }} /><span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>🔍</span></div>
          <button onClick={() => setShowAnalytics(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📊 Analytics</button>
          <button onClick={handleSendReport} disabled={sendingReport} style={{ padding: '0.5rem 1rem', backgroundColor: sendingReport ? '#e5e7eb' : '#eef2ff', color: sendingReport ? '#9ca3af' : '#4338ca', border: '1px solid #c7d2fe', borderRadius: '6px', cursor: sendingReport ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>{sendingReport ? '⏳ Sending...' : '📧 Send Report'}</button>
          <button onClick={() => setIsAddingNew(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>➕ Add Entry</button>
          <button onClick={fetchItems} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>🔄 Refresh</button>
        </div>
      </div>

      <div style={{ backgroundColor: '#d1ecf1', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem', color: '#0c5460' }}>💡 <strong>Tip:</strong> Click any cell to edit. Use this log to track development changes, modifications, and team discussions.</div>

      {isAddingNew && (
        <div style={{ backgroundColor: '#d1ecf1', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #17a2b8' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#0c5460' }}>Add New Development Log Entry</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Timestamp <span style={{ color: '#28a745', fontSize: '0.7rem' }}>(auto)</span></label>
              <input type="text" value={getCurrentTime()} disabled style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', backgroundColor: '#e9ecef', color: '#495057' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>User <span style={{ color: '#28a745', fontSize: '0.7rem' }}>(auto)</span></label>
              <input type="text" value={currentUserName} disabled style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', backgroundColor: '#e9ecef', color: '#495057' }} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Modifications <span style={{ color: '#dc3545' }}>*</span></label>
              <textarea value={newRow.modifications || ''} onChange={(e) => setNewRow(prev => ({ ...prev, modifications: e.target.value }))} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', minHeight: '80px' }} placeholder="Describe what was modified..." />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Team Comments</label>
              <textarea value={newRow.team_comments || ''} onChange={(e) => setNewRow(prev => ({ ...prev, team_comments: e.target.value }))} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', minHeight: '80px' }} placeholder="Any team comments or notes..." />
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={handleAddNewRow} disabled={!newRow.modifications?.trim()} style={{ padding: '8px 16px', backgroundColor: newRow.modifications?.trim() ? '#28a745' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: newRow.modifications?.trim() ? 'pointer' : 'not-allowed' }}>✓ Save</button>
            <button onClick={() => { setIsAddingNew(false); setNewRow({}); }} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✕ Cancel</button>
            <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>Timestamp and User are captured automatically</span>
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: 'white', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
        {/* Fixed Header - Always visible */}
        <table style={{ width: '100%', minWidth: LOG_TABLE_MIN_WIDTH, borderCollapse: 'collapse', fontSize: '0.85rem', tableLayout: 'fixed', flexShrink: 0 }}>
          <thead>
            <tr style={{ backgroundColor: '#17a2b8' }}>
              <th style={{ padding: '10px 6px', textAlign: 'center', width: '50px', borderRight: '1px solid #138496', color: 'white' }}>Actions</th>
              {COLUMNS.map(col => (
                <th key={col.key} style={{ 
                  padding: '10px 8px', 
                  textAlign: 'left', 
                  width: col.fixed ? `${col.width}px` : 'auto',
                  borderRight: '1px solid #138496', 
                  whiteSpace: 'nowrap',
                  color: 'white'
                }}>{col.label}</th>
              ))}
            </tr>
          </thead>
        </table>
        
        {/* Scrollable Body */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0, minWidth: LOG_TABLE_MIN_WIDTH }}>
          <table style={{ width: '100%', minWidth: LOG_TABLE_MIN_WIDTH, borderCollapse: 'collapse', fontSize: '0.85rem', tableLayout: 'fixed' }}>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>
                    {searchTerm ? 'No matching entries' : 'No log entries yet. Click "Add Entry" to create one.'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item, index) => (
                  <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '6px', textAlign: 'center', borderRight: '1px solid #eee', width: '50px' }}>
                      <button onClick={() => handleDeleteRow(item.id)} style={{ padding: '4px 6px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }} title="Delete">🗑️</button>
                    </td>
                    {COLUMNS.map(col => (
                      <td key={col.key} style={{ 
                        padding: '8px', 
                        textAlign: 'left', 
                        borderRight: '1px solid #eee', 
                        backgroundColor: savingRows.has(item.id) ? '#fff3cd' : (index % 2 === 0 ? 'white' : '#f8f9fa'), 
                        verticalAlign: 'top',
                        width: col.fixed ? `${col.width}px` : 'auto'
                      }}>
                        <EditableCell value={item[col.key]} row={item} column={col} onSave={handleSaveCell} disabled={false} accentColor="#17a2b8" />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <DevelopmentAnalyticsDashboard
          data={items}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default DevelopmentLog;


